import { OccupantAddress, UserPermissions } from '@centecomm/portal-service';
import { useTenantBusiness } from '@centecomm/portal-service-react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Occupant } from 'State/Business/Types';
import { LoadStatus } from 'Types';
import { BusinessActions, BusinessActionTypes } from '../actions';
import mapOccupantResponseToBusinessOccupant from '../Mappers/mapOccupantResponseToBusinessOccupant';
import * as selectors from '../selectors';
import useBusinessOccupantsFromState from './useBusinessOccupantsFromState';

type BusinessOccupantsFromStateHook = (
    occupantId: number | string,
    permissions: UserPermissions[],
) => {
    isLoaded: boolean;
    occupant: Occupant;
    mailingAddress: OccupantAddress;
};

const useBusinessOccupantFromState: BusinessOccupantsFromStateHook = (occupantId: number | string, permissions = []) => {
    const { areLoaded: allAreLoaded } = useBusinessOccupantsFromState(permissions);
    const dispatch = useDispatch();
    const { getOccupant } = useTenantBusiness();

    const occupant: Occupant = useSelector(selectors.occupant(occupantId));
    const mailingAddress: OccupantAddress = useSelector(selectors.occupantMailingAddress(occupantId));
    const isOccupantFullyLoaded: boolean = useSelector(selectors.occupantIsFullyLoaded(occupantId));

    React.useEffect(() => {
        if (occupant.loadStatus === LoadStatus.INITIAL_STATE) {
            dispatch({
                type: BusinessActions.LOAD_BUSINESS_OCCUPANT,
            });

            getOccupant(+occupantId)
                .then((fullOccupantData) => {
                    dispatch({
                        type: BusinessActions.SET_BUSINESS_OCCUPANT,
                        payload: mapOccupantResponseToBusinessOccupant(fullOccupantData),
                    } as BusinessActionTypes);
                })
                .catch((error) => {
                    dispatch({
                        type: BusinessActions.SET_BUSINESS_OCCUPANT_ERROR_STATE,
                        payload: {
                            errorMessage: error?.code
                                ? error.code
                                : 'We were not able to load your business information at this time.',
                        },
                    });
                });
        }
    }, [occupant]);

    return {
        isLoaded: allAreLoaded && isOccupantFullyLoaded,
        occupant,
        mailingAddress,
    };
};

export default useBusinessOccupantFromState;
